import SaveIcon from "@mui/icons-material/Save";
import { LoadingButton } from "@mui/lab";
import { Box, Grid2, Typography } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";

import { AGDialog } from "../AGDialog";
import TableSettingsCheckbox from "../TableSettingsCheckbox";

export type ColumnVisibilityType = {
	[columnName: string]: {
		visible: boolean;
		disabled: boolean;
		name: string;
	};
};

interface ITableSettingsDialogProps {
	open: boolean;
	columnVisibility: ColumnVisibilityType;
	saveButtonLoading?: boolean;
	onChangeColumnVisibility: (value: ColumnVisibilityType) => void;
	onClose: () => void;
}

const TableSettingsDialog: FC<ITableSettingsDialogProps> = (props) => {
	const { open, columnVisibility, saveButtonLoading, onChangeColumnVisibility, onClose } = props;

	const [columnVisibilityInputs, setColumnVisibilityInputs] = useState<ColumnVisibilityType>({});

	const selectAllState = useMemo<"none" | "all" | "partial">(() => {
		const values = Object.values(columnVisibilityInputs).filter((item) => !!item.visible);

		if (values.length === 0) return "none";
		if (values.length === Object.keys(columnVisibilityInputs).length) return "all";

		return "partial";
	}, [columnVisibilityInputs]);

	function handleChangeAllColumnsVisibility(checked: boolean) {
		setColumnVisibilityInputs((currentInputs) => {
			const updatedInputs = { ...currentInputs };

			for (const key of Object.keys(updatedInputs)) {
				if (updatedInputs[key].disabled) continue;
				updatedInputs[key].visible = checked;
			}

			return updatedInputs;
		});
	}

	function handleChangeColumnVisibility(key: string, checked: boolean) {
		setColumnVisibilityInputs((currentInputs) => ({
			...currentInputs,
			[key]: {
				...currentInputs[key],
				visible: checked
			}
		}));
	}

	function handleSaveButtonClick() {
		onChangeColumnVisibility(columnVisibilityInputs);
	}

	useEffect(() => {
		if (columnVisibility) {
			setColumnVisibilityInputs(columnVisibility);
		}
	}, [columnVisibility]);

	return (
		<AGDialog open={open} onClose={onClose} title="Table Settings" color="primary">
			<Typography marginBottom="1rem">Select Columns To View</Typography>

			<Grid2 container spacing={1}>
				<Grid2 size={{ xs: 6, md: 4 }}>
					<TableSettingsCheckbox
						label="Select All"
						checked={selectAllState === "all"}
						indeterminate={selectAllState === "partial"}
						onChange={(_event, checked) => handleChangeAllColumnsVisibility(checked)}
					/>
				</Grid2>

				{Object.entries(columnVisibilityInputs).map(([key, item]) => (
					<Grid2 size={{ xs: 6, md: 4 }} key={`column_visibility-${key}`}>
						<TableSettingsCheckbox
							label={item.name}
							checked={item.visible}
							onChange={(_event, checked) => handleChangeColumnVisibility(key, checked)}
							disabled={item.disabled}
						/>
					</Grid2>
				))}

				<Grid2 size={12}>
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
						<LoadingButton
							disableElevation
							variant="contained"
							color="success"
							startIcon={<SaveIcon />}
							loadingPosition="start"
							onClick={handleSaveButtonClick}
							loading={saveButtonLoading}
						>
							Save Changes
						</LoadingButton>
					</Box>
				</Grid2>
			</Grid2>
		</AGDialog>
	);
};

export default TableSettingsDialog;
